import React, { Fragment, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment/moment';
import { connect } from 'react-redux';

import CloseIcon from '@mui/icons-material/Close';
import Tooltip from '@mui/material/Tooltip';
import FiltersIcon from '@mui/icons-material/FilterAltOutlined';
import Box from '@mui/material/Box';

import { styled } from '@mui/material/styles';

import Table from '../shared/table';

import {
  updateCaseDoc,
  sendChangeDateHospitalEmail,
  requestCaseDateUpdate,
  approveRequestDateUpdate,
  rejectRequestDateUpdate,
} from '../../actions/casesActions';

import calendarIcon from '../../assets/calendar_icon_primary.svg';

import userRoles from '../../constants/userRoles';

import './cases.scss';

const StyledTooltip = styled(Tooltip)(({ theme }) => ({
  '& .MuiTooltip-tooltip': {
    padding: theme.spacing(1),
    fontSize: '12px',
    whiteSpace: 'pre-line !important',
  },
}));

const dateTypeLabels = {
  withDate: 'With Date',
  tbc: 'TBC',
};

const hasActiveCaseFilters = (filter = {}) => (
  Object.keys(filter)
    .some((key) => {
      if (key === 'statuses') {
        return Array.isArray(filter[key]) && filter[key].some((value) => value !== 'Open');
      }

      if (key === 'dateType') {
        return filter[key] && filter[key] !== 'all';
      }

      return Array.isArray(filter[key]) ? filter[key].length > 0 : !!filter[key];
    })
);

const getDateFilterLabel = (dateFilter) => (
  `${dateFilter?.from ? moment(dateFilter.from).format('DD/MM/YY') : 'Any'} - ${dateFilter?.to ? moment(dateFilter.to).format('DD/MM/YY') : 'Any'}`
);

const rolesWithCompanyLogoColumn = [
  userRoles.GLOBAL_ADMIN.name,
  userRoles.ENGINEER.name,
  userRoles.SURGEON.name,
  userRoles.MANUFACTURER.name,
  userRoles.LOGISTICS.name,
];

const getCompanyLogoUrl = (logo) => (
  typeof logo === 'string' ? logo : logo?.downloadUrl || ''
);

const CasesTable = ({
  cases = [],
  distributors = [],
  onClick,
  // goToFiles,
  filter,
  filterOptions,
  onFilterChange,
  onRemoveFilterItem,
  radiology = false,
  role,
  procedures,
  hospitals,
  onlyRequired,
  onAdminApproveDateClick,
  onTBCClick,
  onRequestDateClick,
  onAdminApproveConsultationDateClick,
  onRequestConsultationDateClick,
  page,
  setPage,
  rowsPerPage,
  setRowsPerPage,
  totalRows,
  order,
  setOrder,
  orderBy,
  setOrderBy,
  loading,
  companies = []
}) => {
  const renderDateCell = (value, row) => {
    const canDirectlyChangeCaseDate = role === userRoles.GLOBAL_ADMIN.name;
    const hideRequestDate = [userRoles.MANUFACTURER.name, userRoles.LOGISTICS.name, userRoles.RADIOLOGY.name].includes(role);
    const isRadiology = role === userRoles.RADIOLOGY.name;

    if (row.requestedSurgeryDateNew) {
      return (
        <StyledTooltip
          title={`New Case Date Requested – ${moment(row?.requestedSurgeryDateNew).format('DD/MM/YY')}`}
          placement="bottom"
          enterTouchDelay={50}
          leaveTouchDelay={3000}
        >
          <div
            className="pointer p-sm d-flex"
            onClick={(e) => {
              if (canDirectlyChangeCaseDate) {
                e.stopPropagation();
                onAdminApproveDateClick(row);
              }
            }}
          >
          <span>
            {value ? moment(value).format('DD/MM/YYYY') : 'TBC'}
          </span>
            <img style={{ fill: '#000000 !important' }} src={calendarIcon} alt="calendar-icon" height={15}
                 className="m-l-md" />
          </div>
        </StyledTooltip>
      );
    }

    if (!value || !!row.postponed) {
      return (
        <div
          className="primary pointer p-sm"
          onClick={(e) => {
            if (!isRadiology) {
              e.stopPropagation();
              onTBCClick(row);
            }
          }}
        >
          TBC
        </div>
      );
    }

    return (
      (canDirectlyChangeCaseDate || hideRequestDate) ? (
        moment(value).format('DD/MM/YYYY')
      ) : (
        <StyledTooltip
          title='Request New Surgery Date'
          placement="bottom"
          enterTouchDelay={50}
          leaveTouchDelay={3000}
        >
          <div
            className="primary pointer p-sm"
            onClick={(e) => {
              e.stopPropagation();
              onRequestDateClick(row);
            }}
          >
            {moment(value).format('DD/MM/YYYY')}
          </div>
        </StyledTooltip>
      )
    );
  }

  const renderConsultationDateCell = (value, row) => {
    const isAdmin = [userRoles.GLOBAL_ADMIN.name, userRoles.COUNTRY_ADMIN.name, userRoles.DISTRIBUTOR_ADMIN.name]?.includes(role);
    const requestedConsultationDate = row?.requestedConsultationDate?.newDate || row?.requestedConsultationDateNew;

    if (requestedConsultationDate) {
      return (
        <StyledTooltip
          title={`New Consultation Date Requested – ${moment(requestedConsultationDate).format('DD/MM/YY')}`}
          placement="bottom"
          enterTouchDelay={50}
          leaveTouchDelay={3000}
        >
          <div
            className="pointer p-sm d-flex"
            onClick={(e) => {
              if (isAdmin) {
                e.stopPropagation();
                onAdminApproveConsultationDateClick(row);
              }
            }}
          >
          <span>
            {value ? moment(value).format('DD/MM/YYYY') : 'TBC'}
          </span>
            <img style={{ fill: '#000000 !important' }} src={calendarIcon} alt="calendar-icon" height={15}
                 className="m-l-md" />
          </div>
        </StyledTooltip>
      );
    }

    if (!value) {
      return '';
    }

    return (
      isAdmin ? (
        moment(value).format('DD/MM/YYYY')
      ) : (
        <StyledTooltip
          title='Request New Consultation Date'
          placement="bottom"
          enterTouchDelay={50}
          leaveTouchDelay={3000}
        >
          <div
            className="primary pointer p-sm"
            onClick={(e) => {
              e.stopPropagation();
              onRequestConsultationDateClick(row);
            }}
          >
            {moment(value).format('DD/MM/YYYY')}
          </div>
        </StyledTooltip>
      )
    );
  }

  const columns = useMemo(() => {
    const shouldShowCompanyLogoColumn = rolesWithCompanyLogoColumn.includes(role);
    const companyLogoColumn = shouldShowCompanyLogoColumn ? [{
      title: '',
      field: 'company',
      type: 'custom',
      filter: false,
      width: 58,
      formatter: (value) => {
        const company = companies?.find((item) => item.id === value);
        const logoUrl = getCompanyLogoUrl(company?.logo);

        if (!logoUrl) {
          return null;
        }

        return (
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <Box
              component="img"
              src={logoUrl}
              alt={`${company?.name || 'company'}-logo`}
              sx={{
                width: 30,
                height: 30,
                objectFit: 'contain',
                borderRadius: '8px',
                border: '1px solid var(--color-border)',
                backgroundColor: 'var(--color-surface)',
                p: '2px',
              }}
            />
          </Box>
        );
      },
    }] : [];

    if (radiology) {
      return [
        { title: '', field: 'access', type: 'indicator' },
        { title: 'Case ID', field: 'formattedId', type: 'default', sort: true },
        { title: 'Patient', field: 'patientLastName', type: 'default', sort: true },
        {
          title: 'Consultation',
          field: 'consultationDate',
          type: 'custom',
          formatter: renderConsultationDateCell,
          sort: true,
        },
        {
          title: 'Surgery',
          field: 'date',
          type: 'custom',
          formatter: renderDateCell,
          sort: true,
        },
        { title: 'Surgeon', field: 'surgeonName', type: 'default', sort: true },
        { title: 'Procedure', field: 'procedureName', type: 'default', sort: true },
        { title: 'Action', field: 'action', type: 'caseAction' },
        { title: 'Step', field: 'step', type: 'step' },
        ...companyLogoColumn,
        // { title: 'scans', field: 'scan', type: 'file', icon: scansIcon, onClick: goToFiles },
      ];
    }
    return [
      { title: '', field: 'access', type: 'indicator' },
      { title: 'Case ID', field: 'formattedId', type: 'default', sort: true },
      { title: 'Patient', field: 'patientLastName', type: 'default', sort: true },
      {
        title: 'Consultation',
        field: 'consultationDate',
        type: 'custom',
        formatter: renderConsultationDateCell,
        sort: true,
      },
      {
        title: 'Surgery',
        field: 'date',
        type: 'custom',
        formatter: renderDateCell,
        sort: true,
      },
      { title: 'Surgeon', field: 'surgeon', type: 'default', sort: true },
      { title: 'Hospital', field: 'hospital', type: 'default', sort: true },
      { title: 'Country', field: 'country', type: 'default', sort: true },
      { title: 'Procedure', field: 'procedure', type: 'default', sort: true },
      { title: 'Action', field: 'action', type: 'caseAction' },
      { title: 'Step', field: 'step', type: 'step', sort: true },
      ...companyLogoColumn,
      // { title: 'scans', field: 'scan', type: 'file', icon: scansIcon, onClick: goToFiles },
      // { title: 'preop', field: 'preop', type: 'file', icon: preopIcon, onClick: goToFiles },
      // { title: 'block', field: 'block', type: 'file', icon: blockIcon, onClick: goToFiles }
    ];
  }, [cases, radiology, companies]);

  const handleClick = (row) => {
    onClick(row);
  };

  const getCountryName = (distributorId) => {
    const distributor = distributors?.find((d) => d.id === distributorId);
    return distributor?.country || '';
  };

  const getFormattedId = (item) => {
    if (!item || !item.id) {
      return '';
    }
    if (item?.formattedId) {
      return item.formattedId;
    }
    const arr = item?.id?.split('_');
    return arr?.length > 1 ? arr[0] : item?.id;
  };

  // const getSortDate = (item) => {
  //   if (item.date) {
  //     return item.date;
  //   }
  //
  //   const procedure = procedures?.find((p) => p.id === item.procedure);
  //   const leadDays = procedure ? (procedure?.leadDays || 0) : defaultLeadDays;
  //
  //   return moment(item.createdAt)?.add(leadDays, 'days')?.format() || null;
  // };

  // const rows = sortBy(cases?.map((item) => ({
  //   ...item,
  //   sortDate: getSortDate(item),
  //   formattedId: getFormattedId(item),
  //   country: getCountryName(item.distributorId),
  // })), 'sortDate');

  const rows = useMemo(() => (
    cases?.map((item) => ({
      ...item,
      formattedId: getFormattedId(item),
      country: getCountryName(item.distributorId),
    })
  )), [cases]);

  const formatValue = useCallback((key, value) => {
    if (key === 'steps') {
      return value?.label;
    }
    if (key === 'companies') {
      const company = companies.find((item) => item.id === value);
      return company?.name || '';
    }
    // if (key === 'hospitals') {
    //   const hospital = hospitals.find((h) => h.id === value);
    //   return hospital?.name || '';
    // }
    // if (key === 'procedures') {
    //   const procedure = procedures.find((h) => h.id === value);
    //   return procedure?.name || '';
    // }

    return value;
  }, [companies, hospitals, procedures]);

  const renderFilterList = () => {
    if (!filter || !hasActiveCaseFilters(filter)) {
      return null;
    }

    const renderFilterChip = (key, label, value) => (
      <div key={value ? `${key}-${value}` : key} className="filter-list-item">
        <div />
        <div className="filter-list-item-value">{label}</div>
        <CloseIcon
          color="secondary"
          onClick={() => {
            if (!loading) {
              onRemoveFilterItem(key, value);
            }
          }}
        />
      </div>
    );

    const renderFilterGroup = (key) => {
      if (key === 'date' && filter.date) {
        return (
          <div key={key} className="filter-list-item-container">
            {renderFilterChip(key, getDateFilterLabel(filter.date))}
          </div>
        );
      }

      if (key === 'dateType' && filter.dateType && filter.dateType !== 'all') {
        return (
          <div key={key} className="filter-list-item-container">
            {renderFilterChip(key, dateTypeLabels[filter.dateType] || filter.dateType)}
          </div>
        );
      }

      if (!Array.isArray(filter[key])) {
        return null;
      }

      return (
        <div key={key} className="filter-list-item-container">
          {filter[key]
            .filter((value) => value !== 'Open')
            .map((value) => renderFilterChip(key, formatValue(key, value), value))}
        </div>
      );
    };

    return (
      <div className="filter-list">
        <FiltersIcon color="secondary" />
        {Object.keys(filter).map(renderFilterGroup)}
      </div>
    );
  };

  return (
    <Fragment>
      {renderFilterList()}
      <Table
        stickyTable
        rows={onlyRequired ? rows.slice(0,5) : rows}
        columns={columns}
        onRowClick={handleClick}
        onFilterChange={onFilterChange}
        currentFilter={filter}
        filterOptions={filterOptions}
        withoutPagination={onlyRequired}
        page={page}
        setPage={setPage}
        order={order}
        setOrder={setOrder}
        orderBy={orderBy}
        setOrderBy={setOrderBy}
        rowsPerPage={rowsPerPage}
        setRowsPerPage={setRowsPerPage}
        totalRows={totalRows}
        loading={loading}
      />
    </Fragment>
  );
};

CasesTable.propTypes = {
  cases: PropTypes.array,
  filter: PropTypes.object,
  filterOptions: PropTypes.object,
  onFilterChange: PropTypes.func,
  onRemoveFilterItem: PropTypes.func,
  onClick: PropTypes.func,
  goToFiles: PropTypes.func,
};

const mapStateToProps = (state) => {
  return {
    hospitals: state?.hospitals?.list || [],
    surgeons: state?.surgeons?.list || [],
    procedures: state?.procedures?.list || [],
    role: state.user.currentUser.role,
    engineerLevel: state.user.currentUser.engineerLevel,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    updateCaseDoc: (caseId, data) => dispatch(updateCaseDoc(caseId, data)),
    sendChangeDateHospitalEmail: (caseId, data, previousData) => dispatch(sendChangeDateHospitalEmail(caseId, data, previousData)),
    requestCaseDateUpdate: (activeCase, date, hospital) => dispatch(requestCaseDateUpdate(activeCase, date, hospital)),
    approveRequestDateUpdate: (activeCase) => dispatch(approveRequestDateUpdate(activeCase)),
    rejectRequestDateUpdate: (activeCase) => dispatch(rejectRequestDateUpdate(activeCase))
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(CasesTable);
